import { IonIcon } from '@ionic/react'
import { msg, plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  alertCircleOutline,
  cameraOutline,
  chevronForwardOutline,
  cloudUploadOutline,
} from 'ionicons/icons'
import { useHistory } from 'react-router-dom'
import { useQuery } from 'urql'
import { usePhotoUpload } from '../../context/PhotoUploadProvider'
import { GetOrderPhotoCount } from '../../graphql/orderPhotos'
import { entriesForOrder } from '../../helpers/orderPhotoQueueView'

type OrderPhotosRowProps = {
  orderId: number
  assignmentId?: string
}

export const OrderPhotosRow = ({
  orderId,
  assignmentId,
}: OrderPhotosRowProps) => {
  const { _ } = useLingui()
  const history = useHistory()

  const [result] = useQuery({
    query: GetOrderPhotoCount,
    variables: { orderId },
    // Count stays fresh; cached entry renders instantly on remount
    requestPolicy: 'cache-and-network',
  })
  const { pendingPhotos, recentlyUploaded } = usePhotoUpload()

  // Counts only — no blob/object-URL loading here (that's the photos screen)
  const serverPhotos = result.data?.order?.photos ?? []
  const queuedForOrder = entriesForOrder(pendingPhotos, orderId, serverPhotos)
  // Uploaded but not yet in the stale server list — still one photo
  const uploadedAwaiting = entriesForOrder(
    recentlyUploaded,
    orderId,
    serverPhotos,
  )
  const count =
    serverPhotos.length + queuedForOrder.length + uploadedAwaiting.length
  const pendingCount = queuedForOrder.length
  // A stuck photo must not read as merely queued
  const hasFailed = queuedForOrder.some((entry) => entry.state === 'failed')

  const photosPath = assignmentId
    ? `/assignments/${assignmentId}/orders/${orderId}/photos`
    : `/orders/${orderId}/photos`

  return (
    <button
      type='button'
      className='order-photos-row'
      onClick={() => history.push(photosPath)}
    >
      <IonIcon icon={cameraOutline} className='order-photos-row__icon' />
      <span className='order-photos-row__label'>{_(msg`Photos`)}</span>
      {count > 0 && <span className='order-photos-row__count'>{count}</span>}
      {pendingCount > 0 && (
        <output
          className={`order-photos-row__pending ${
            hasFailed ? 'order-photos-row__pending--failed' : ''
          }`}
          aria-label={
            hasFailed
              ? _(
                  plural(pendingCount, {
                    one: '# photo failed to upload, will retry',
                    other: '# photos failed to upload, will retry',
                  }),
                )
              : _(
                  plural(pendingCount, {
                    one: '# photo pending upload',
                    other: '# photos pending upload',
                  }),
                )
          }
        >
          <IonIcon icon={hasFailed ? alertCircleOutline : cloudUploadOutline} />
          {pendingCount}
        </output>
      )}
      <IonIcon
        icon={chevronForwardOutline}
        className='order-photos-row__chevron'
      />
    </button>
  )
}
